import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format, parseISO } from 'date-fns';
import { hu } from 'date-fns/locale';
import { formatServerDateTime } from '../../utils/dateUtils';
import coachPaymentService from '../../services/coachPaymentService';
import {
  CoachPayment,
  PaymentStatus,
  PaymentStatusDisplayNames,
  PaymentStatusColors,
  PaymentItemCategory,
  PaymentItemCategoryDisplayNames
} from '../../types/coachPayment';
import { useFeedback } from '../../contexts/FeedbackContext';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';

/**
 * MyPayments Component - Coach view for their own payments
 */
const MyPayments: React.FC = () => {
  const { showSuccess, showError } = useFeedback();
  const [payments, setPayments] = useState<CoachPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<CoachPayment | null>(null);
  const [coachNotes, setCoachNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await coachPaymentService.getMyPayments();
      setPayments(data);
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a kifizetések betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmDialog = (payment: CoachPayment) => {
    setSelectedPayment(payment);
    setCoachNotes('');
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedPayment(null);
    setCoachNotes('');
  };

  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] Lelet: `MyPayments.tsx:313-314`
   * (korábbi sorok) — az edző visszaigazoló „Megjegyzés (opcionális)” szövege a háttér-kattintásra/
   * Esc-re/„Mégse”-re kérdés nélkül elveszett. Piszkos = nem üres (trim után); az üres mező NEM
   * kérdez. A hook a `loading` korai visszatérése ELŐTT áll.
   */
  const confirm = useConfirm();
  const notesDirty = confirmDialogOpen && isFormDirty({ coachNotes: '' }, { coachNotes });
  useUnsavedChanges(notesDirty, 'Kifizetés igazolása');

  const requestCloseConfirmDialog = async () => {
    if (submitting) return;
    if (notesDirty && !(await confirm(discardChangesConfirm('megjegyzés')))) return;
    handleCloseConfirmDialog();
  };

  const handleConfirmPayment = async () => {
    if (!selectedPayment) return;

    try {
      setSubmitting(true);
      await coachPaymentService.confirmPayment(selectedPayment.paymentId, {
        coachNotes: coachNotes || undefined
      });
      await loadPayments();
      handleCloseConfirmDialog();
      showSuccess('A kifizetés visszaigazolva.');
    } catch (err: any) {
      // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat, nem a nyers 401
      const message = saveErrorMessage(err, 'Hiba történt a jóváhagyás során');
      setError(message);
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return format(parseISO(dateString), 'yyyy. MM. dd.', { locale: hu });
  };

  // [Web UX 1. csomag (4.3), 2026-10-03] UTC, „Z” nélkül (élő API-n mérve) → közös segéd; a dátum-csak
  // `formatDate` (fent) helyes, mert a `parseISO('yyyy-MM-dd')` helyi éjfelet ad.
  const formatDateTime = (dateString: string) => formatServerDateTime(dateString, { fallback: '-' });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Saját Kifizetéseim
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {payments.length === 0 ? (
        <Alert severity="info">
          Még nincsenek kifizetések rögzítve.
        </Alert>
      ) : (
        <Box sx={{ mt: 2 }}>
          {payments.map((payment) => (
            <Accordion key={payment.paymentId} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {payment.coachName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                    <Typography variant="body2">
                      {payment.totalHours} óra • {formatCurrency(payment.totalAmount)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                    <Chip
                      label={PaymentStatusDisplayNames[payment.status as PaymentStatus]}
                      color={PaymentStatusColors[payment.status as PaymentStatus] as any}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                    {payment.status === 'PaidByAdmin' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenConfirmDialog(payment);
                        }}
                      >
                        Igazolom
                      </Button>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {/* Payment Details */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' } }}>
                      <Typography variant="body2" color="text.secondary">
                        Létrehozva:
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(payment.createdDate)} ({payment.createdByName})
                      </Typography>
                    </Box>
                    {payment.paidDate && (
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' } }}>
                        <Typography variant="body2" color="text.secondary">
                          Kifizetve:
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(payment.paidDate)} ({payment.paidByName})
                        </Typography>
                      </Box>
                    )}
                    {payment.confirmedDate && (
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%' } }}>
                        <Typography variant="body2" color="text.secondary">
                          Visszaigazolva:
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(payment.confirmedDate)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Notes */}
                  {payment.adminNotes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Admin megjegyzés:
                      </Typography>
                      <Typography variant="body1">{payment.adminNotes}</Typography>
                    </Box>
                  )}
                  {payment.coachNotes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Saját megjegyzés:
                      </Typography>
                      <Typography variant="body1">{payment.coachNotes}</Typography>
                    </Box>
                  )}

                  {/* Events Table */}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Kifizetési tételek ({payment.paymentEvents.length} db)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Kategória</TableCell>
                          <TableCell>Dátum</TableCell>
                          <TableCell>Esemény/Leírás</TableCell>
                          <TableCell>Időpont</TableCell>
                          <TableCell align="right">Órák</TableCell>
                          <TableCell align="right">Összeg</TableCell>
                          <TableCell align="center">Jelenlét</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payment.paymentEvents.map((event) => (
                          <TableRow key={event.paymentEventId}>
                            <TableCell>
                              <Chip
                                size="small"
                                label={PaymentItemCategoryDisplayNames[event.category as PaymentItemCategory] || event.category}
                                color={event.category === 'Training' ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{formatDate(event.eventDate)}</TableCell>
                            <TableCell>
                              {event.isManualEntry && event.description ? event.description : event.eventName}
                            </TableCell>
                            <TableCell>
                              {event.startTime && event.endTime
                                ? `${event.startTime.substring(0, 5)} - ${event.endTime.substring(0, 5)}`
                                : '-'}
                            </TableCell>
                            <TableCell align="right">{event.hours || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(event.amount)}</TableCell>
                            <TableCell align="center">
                              {event.category === 'Training' ? (
                                event.coachAttended ? (
                                  <Chip label="Jelen" color="success" size="small" />
                                ) : (
                                  <Chip label="Hiányzott" color="error" size="small" />
                                )
                              ) : (
                                <Chip label="N/A" size="small" color="default" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Confirm Payment Dialog */}
      <Dialog open={confirmDialogOpen} onClose={requestCloseConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Kifizetés igazolása</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                Kifizetés összege: <strong>{formatCurrency(selectedPayment.totalAmount)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Időszak: {formatDate(selectedPayment.periodStart)} - {formatDate(selectedPayment.periodEnd)}
              </Typography>
              <TextField
                label="Megjegyzés (opcionális)"
                multiline
                rows={3}
                fullWidth
                value={coachNotes}
                onChange={(e) => setCoachNotes(e.target.value)}
                placeholder="Pl. Megérkezett, köszönöm!"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={requestCloseConfirmDialog} disabled={submitting}>
            Mégse
          </Button>
          <Button
            onClick={handleConfirmPayment}
            variant="contained"
            color="success"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {submitting ? 'Feldolgozás...' : 'Igazolom'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPayments;
